import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Row, Col } from 'react-bootstrap';
import DateRangePicker from 'react-bootstrap-daterangepicker';
import { toast } from 'react-toastify';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  Cell,
  LabelList,
  Legend,
  CartesianGrid,
} from 'recharts';
import { getTaskAnalytics } from '../../services/PerformanceService';
import {
  PERF_CHART,
  STATUS_BAR_FILLS,
  DUE_BAR_FILLS,
  MONTH_STATUS_COLORS,
  MONTH_STATUS_KEYS,
  CHART_AXIS_TICK,
  CHART_MARGIN,
  CHART_GRID_PROPS,
  ChartGradientDefs,
  PerformanceChartTooltip,
  ChartValueLabel,
} from './performanceChartConfig';
import 'bootstrap-daterangepicker/daterangepicker.css';
import './tasklist.css';

function formatDateLocalForApi(date) {
  if (!date) return '';
  const d = date instanceof Date ? date : new Date(date);
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

function defaultDateFilters() {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const rangeStart = new Date(today);
  rangeStart.setMonth(rangeStart.getMonth() - 12);
  const rangeEnd = new Date(today);
  rangeEnd.setMonth(rangeEnd.getMonth() + 6);
  return {
    startDateFrom: rangeStart,
    startDateTo: rangeEnd,
    endDateFrom: null,
    endDateTo: null,
  };
}

const BAR_RADIUS = [8, 8, 0, 0];
const ACTIVE_BAR = { fill: PERF_CHART.accentLight, opacity: 0.92 };

function ChartEmpty({ message }) {
  return (
    <div className="performance-chart-empty">
      <i className="fas fa-chart-column" aria-hidden />
      <p>{message}</p>
    </div>
  );
}

function ChartPanel({ title, subtitle, children }) {
  return (
    <div className="performance-glass-panel performance-chart-panel h-100">
      <div className="performance-chart-panel__head">
        <h6 className="performance-chart-label mb-0">{title}</h6>
        {subtitle ? <span className="performance-chart-panel__sub">{subtitle}</span> : null}
      </div>
      <div className="performance-chart-panel__body">{children}</div>
    </div>
  );
}

const PerformanceTaskAnalytics = () => {
  const [filters, setFilters] = useState(defaultDateFilters);
  const [loading, setLoading] = useState(true);
  const [analytics, setAnalytics] = useState(null);
  const [loadError, setLoadError] = useState(null);

  const loadAnalytics = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const params = {};
      if (filters.startDateFrom && filters.startDateTo) {
        params.start_date_from = formatDateLocalForApi(filters.startDateFrom);
        params.start_date_to = formatDateLocalForApi(filters.startDateTo);
      }
      if (filters.endDateFrom && filters.endDateTo) {
        params.end_date_from = formatDateLocalForApi(filters.endDateFrom);
        params.end_date_to = formatDateLocalForApi(filters.endDateTo);
      }
      const res = await getTaskAnalytics(params);
      setAnalytics(res.data?.data ?? null);
    } catch (err) {
      console.error(err);
      setAnalytics(null);
      const status = err.response?.status;
      const msg =
        err.response?.data?.message ||
        (status === 404
          ? 'Analytics API not found — restart the backend server.'
          : 'Failed to load task analytics');
      setLoadError(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    loadAnalytics();
  }, [loadAnalytics]);

  const byStatus = analytics?.byStatus ?? [];
  const byDue = useMemo(
    () =>
      (analytics?.byDue ?? []).map((row) => ({
        ...row,
        fill: DUE_BAR_FILLS[row.key] || row.color,
      })),
    [analytics?.byDue]
  );
  const byMonthStatus = analytics?.byMonthStatus ?? [];
  const hasDueData = byDue.some((d) => d.count > 0);

  return (
    <div className="performance-analytics-section">
      <hr className="performance-analytics-divider" />

      <Row className="g-2 mb-2 performance-analytics-filters">
        <Col md={6}>
          <label className="form-label small fw-semibold mb-1">Task date</label>
          <DateRangePicker
            initialSettings={{
              startDate: filters.startDateFrom,
              endDate: filters.startDateTo,
              locale: { format: 'MM/DD/YYYY' },
              autoApply: true,
            }}
            onApply={(event, picker) => {
              if (picker.startDate && picker.endDate) {
                setFilters((prev) => ({
                  ...prev,
                  startDateFrom: picker.startDate.toDate(),
                  startDateTo: picker.endDate.toDate(),
                }));
              }
            }}
          >
            <input
              type="text"
              className="form-control input-daterange-timepicker"
              readOnly
              placeholder="Task start date range"
            />
          </DateRangePicker>
        </Col>
        <Col md={6}>
          <label className="form-label small fw-semibold mb-1">End date (due)</label>
          <DateRangePicker
            initialSettings={{
              ...(filters.endDateFrom && filters.endDateTo
                ? { startDate: filters.endDateFrom, endDate: filters.endDateTo }
                : {}),
              locale: { format: 'MM/DD/YYYY' },
              autoApply: true,
            }}
            onApply={(event, picker) => {
              if (picker.startDate && picker.endDate) {
                setFilters((prev) => ({
                  ...prev,
                  endDateFrom: picker.startDate.toDate(),
                  endDateTo: picker.endDate.toDate(),
                }));
              }
            }}
          >
            <input
              type="text"
              className="form-control input-daterange-timepicker"
              readOnly
              placeholder="Due date range"
            />
          </DateRangePicker>
        </Col>
      </Row>

      {!loading && analytics?.totalTasks != null ? (
        <p className="performance-period-context performance-analytics-context mb-2">
          <i className="fas fa-tasks me-1" aria-hidden />
          <span>{analytics.totalTasks} tasks in selected range</span>
        </p>
      ) : null}

      {loading ? (
        <div className="loading-container performance-analytics-loading">
          <div className="loading-spinner" />
        </div>
      ) : loadError ? (
        <p className="text-danger mb-0">{loadError}</p>
      ) : (
        <Row className="g-2 performance-charts-row performance-charts-two-col">
          <Col xs={12} md={6} className="performance-chart-col">
            <ChartPanel title="By status" subtitle="Open & completed mix">
              {byStatus.length === 0 ? (
                <ChartEmpty message="No tasks match these filters." />
              ) : (
                <ResponsiveContainer width="100%" height={200}>
                  <BarChart data={byStatus} margin={CHART_MARGIN.simple} barCategoryGap="28%">
                    <ChartGradientDefs />
                    <CartesianGrid {...CHART_GRID_PROPS} />
                    <XAxis
                      dataKey="label"
                      tick={CHART_AXIS_TICK}
                      axisLine={{ stroke: 'rgba(15,23,42,0.08)' }}
                      tickLine={false}
                      interval={0}
                      angle={-22}
                      textAnchor="end"
                      height={48}
                    />
                    <YAxis
                      allowDecimals={false}
                      tick={CHART_AXIS_TICK}
                      axisLine={false}
                      tickLine={false}
                      width={32}
                    />
                    <Tooltip
                      content={<PerformanceChartTooltip />}
                      cursor={{ fill: 'rgba(196, 33, 38, 0.06)', radius: 6 }}
                    />
                    <Bar
                      dataKey="count"
                      radius={BAR_RADIUS}
                      maxBarSize={44}
                      activeBar={ACTIVE_BAR}
                    >
                      {byStatus.map((entry, i) => (
                        <Cell
                          key={entry.label}
                          fill={STATUS_BAR_FILLS[i % STATUS_BAR_FILLS.length]}
                        />
                      ))}
                      <LabelList dataKey="count" content={<ChartValueLabel />} />
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              )}
            </ChartPanel>
          </Col>

          <Col xs={12} md={6} className="performance-chart-col">
            <ChartPanel title="By due" subtitle="Overdue & upcoming">
              {!hasDueData ? (
                <ChartEmpty message="No tasks match these filters." />
              ) : (
                <ResponsiveContainer width="100%" height={200}>
                  <BarChart data={byDue} margin={CHART_MARGIN.simple} barCategoryGap="28%">
                    <ChartGradientDefs />
                    <CartesianGrid {...CHART_GRID_PROPS} />
                    <XAxis
                      dataKey="label"
                      tick={CHART_AXIS_TICK}
                      axisLine={{ stroke: 'rgba(15,23,42,0.08)' }}
                      tickLine={false}
                      interval={0}
                      angle={-18}
                      textAnchor="end"
                      height={44}
                    />
                    <YAxis
                      allowDecimals={false}
                      tick={CHART_AXIS_TICK}
                      axisLine={false}
                      tickLine={false}
                      width={32}
                    />
                    <Tooltip
                      content={<PerformanceChartTooltip />}
                      cursor={{ fill: 'rgba(196, 33, 38, 0.06)', radius: 6 }}
                    />
                    <Bar dataKey="count" radius={BAR_RADIUS} maxBarSize={44}>
                      {byDue.map((entry) => (
                        <Cell key={entry.key} fill={entry.fill} />
                      ))}
                      <LabelList dataKey="count" content={<ChartValueLabel />} />
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              )}
            </ChartPanel>
          </Col>

          <Col xs={12} md={6} className="performance-chart-col">
            <ChartPanel title="By month & status" subtitle="Stacked timeline">
              {byMonthStatus.length === 0 ? (
                <ChartEmpty message="No tasks match these filters." />
              ) : (
                <ResponsiveContainer width="100%" height={240}>
                  <BarChart data={byMonthStatus} margin={CHART_MARGIN.stacked} barCategoryGap="20%">
                    <CartesianGrid {...CHART_GRID_PROPS} />
                    <XAxis
                      dataKey="monthLabel"
                      tick={CHART_AXIS_TICK}
                      axisLine={{ stroke: 'rgba(15,23,42,0.08)' }}
                      tickLine={false}
                      interval={0}
                      angle={-28}
                      textAnchor="end"
                      height={56}
                    />
                    <YAxis
                      allowDecimals={false}
                      tick={CHART_AXIS_TICK}
                      axisLine={false}
                      tickLine={false}
                      width={32}
                    />
                    <Tooltip
                      content={<PerformanceChartTooltip />}
                      cursor={{ fill: 'rgba(196, 33, 38, 0.04)' }}
                    />
                    <Legend
                      iconType="circle"
                      iconSize={8}
                      wrapperStyle={{ fontSize: 10, paddingTop: 4 }}
                    />
                    {MONTH_STATUS_KEYS.map((key) => (
                      <Bar
                        key={key}
                        dataKey={key}
                        name={key}
                        stackId="status"
                        fill={MONTH_STATUS_COLORS[key]}
                        radius={[2, 2, 0, 0]}
                        maxBarSize={40}
                      />
                    ))}
                  </BarChart>
                </ResponsiveContainer>
              )}
            </ChartPanel>
          </Col>
        </Row>
      )}
    </div>
  );
};

export default PerformanceTaskAnalytics;
